import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import React, { useEffect, useMemo, useRef, useState } from "react";

import Loader from "../../../Loader/Loader";
import { getDeviceList } from "../../telemetry-utils";
import { trimAssetName } from "../../../../Common/EndPoints";
import { useGetTelemetryData } from "../../useGetTelemetryData";
import { ReactComponent as PointMachineCircuitDiagram } from "../pm_circuit_diagram.svg";
import { selectedCustomersIdsSelector } from "../../../../features/authentication/auth-slice";
import { ReactComponent as PointMachineParallelCircuitDiagram } from "../pm_circuit_parallel.svg";
import { ReactComponent as PointMachineSingleEndCircuitDiagram } from "../pm_circuit_diagram_single_end.svg";
import {
  getDeviceTypeForLabel,
  getParallelPointMachineSvgDataMap,
  getPointMachineInformation,
  getSeriesPointMachineSvgDataMap,
  getSingleEndPointMachineSvgDataMap,
} from "./pm-circuit-view-utils";

type PointMachineType = "Series" | "Parallel" | "Single End";
type PointMachineInformation =
  | {
      end: "A" | "B" | "Single End";
      type: PointMachineType;
      connected: string;
      connected_id?: string;
    }
  | undefined;
interface Props {
  open: boolean;
  gear_type: string;
  device_id: string;
  handleClose: () => void;
}
const PointMachineCircuitViewWrapper: React.FC<Props> = (props) => {
  const { device_id, gear_type } = props;
  const customerIdList = useSelector(selectedCustomersIdsSelector) as string[];
  const [state, setState] = useState<any>({});
  const [infoLoading, setInfoLoading] = useState(false);

  const telemetryInfo = useMemo(() => {
    if (infoLoading) return { skip: true, required_device_id_list: [] };
    if (state.type === "Single End") {
      return { skip: false, required_device_id_list: [device_id] };
    }
    if (!("device_id_of_pm_on_end_a" in state) && !("device_id_of_pm_on_end_b" in state)) {
      return { skip: true, required_device_id_list: [] };
    }
    return { skip: false, required_device_id_list: [state?.device_id_of_pm_on_end_a, state?.device_id_of_pm_on_end_b] };
  }, [infoLoading, state]);

  const { data, loading: telemetry_state_loading } = useGetTelemetryData({
    deviceCategory: gear_type,
    skip: telemetryInfo.skip,
    required_device_id_list: telemetryInfo.required_device_id_list,
  });

  const svg_data_map = useMemo(() => {
    if (
      Object.keys(data).length === 0 ||
      telemetry_state_loading ||
      infoLoading ||
      Object.keys(state).length === 0 ||
      !state.type
    )
      return {};

    switch (state.type) {
      case "Series":
        return getSeriesPointMachineSvgDataMap(data, state?.device_id_of_pm_on_end_a, state?.device_id_of_pm_on_end_b);
      case "Parallel":
        return getParallelPointMachineSvgDataMap(
          data,
          state?.device_id_of_pm_on_end_a,
          state?.device_id_of_pm_on_end_b,
        );
      case "Single End":
        return getSingleEndPointMachineSvgDataMap(data, device_id);
      default:
        return {};
    }
  }, [data, infoLoading, telemetry_state_loading, state]);

  useEffect(() => {
    async function getRequiredData() {
      setInfoLoading(true);
      try {
        const pm_type = await getDeviceTypeForLabel(gear_type);
        const pm_type_id = pm_type.typeid;
        const pm_info: PointMachineInformation = await getPointMachineInformation(device_id, pm_type_id);

        if (!pm_info) return;

        if (pm_info.type === "Single End") {
          setState({
            device_id_of_single_end_pm: device_id,
            type: pm_info.type,
          });
          return;
        }

        if (!pm_info.connected) return;
        const device_list_raw = await getDeviceList({
          params: { deviceTypeIds: pm_type_id, customerIds: customerIdList.toString() },
        });
        const device_list = device_list_raw.data.data;
        for (const device of device_list) {
          const name = trimAssetName(device.name);
          if (name === pm_info.connected) {
            pm_info.connected_id = device.deviceid;
            setState({
              device_id_of_pm_on_end_a: pm_info.end === "A" ? device_id : device.deviceid,
              device_id_of_pm_on_end_b: pm_info.end === "B" ? device_id : device.deviceid,
              type: pm_info.type,
            });
            break;
          }
        }
      } catch (error: any) {
        console.error("POINT_MACHINE_GET_INFORMATION", error);
        toast.error(error?.message ?? "Information not available for this Point Machine");
      } finally {
        setInfoLoading(false);
      }
    }

    getRequiredData();
  }, []);

  return (
    <div style={{ height: "100%", width: "100%", overflow: "auto" }}>
      {infoLoading || telemetry_state_loading ? (
        <Loader />
      ) : (
        <TelemetryCircuitView data={data} gear_type={gear_type} data_map={svg_data_map} type={state.type} />
      )}
    </div>
  );
};
type PacketData = Record<string, [{ ts: string; value: string }]>;
type ParsedData = Record<string, PacketData>;
type PopulateLogicType = { divide_by?: number; unit?: string; show_time_on_hover?: boolean };
type SvgDataMapType = Record<
  string,
  {
    key: string;
    device_id: string;
    populate_element?: (
      element: HTMLElement,
      data: PacketData | ParsedData,
      key: string,
      config: PopulateLogicType,
    ) => void;
  } & PopulateLogicType
>;

type CircuitViewProps = {
  data: Record<string, PacketData>;
  data_map: SvgDataMapType;
  gear_type: string;
  type: PointMachineType;
};
const TelemetryCircuitView: React.FC<CircuitViewProps> = (props) => {
  const svg_ref = useRef<SVGSVGElement | null>(null);
  const { data = {}, data_map, type } = props;
  useEffect(() => {
    const svg_component = svg_ref.current;
    if (!svg_component) return;
    for (const svg_element_id in data_map) {
      const element = svg_component.getElementById(svg_element_id) as HTMLElement;
      const populate_logic = data_map[svg_element_id];
      const device_id = populate_logic.device_id;
      const device_data = data[device_id];

      if (!element) {
        console.error("SVG_ELEMENT_NOT_FOUND_FOR_ID", svg_element_id);
        continue;
      }

      if (!device_data) {
        if (!device_id && populate_logic.populate_element) {
          populate_logic.populate_element(element, data, populate_logic.key, populate_logic);
          continue;
        }

        console.error("DEVICE_DATA_NOT_FOUND_TO_POPULATE_SVG", device_id);
        continue;
      }

      if (populate_logic.populate_element) {
        populate_logic.populate_element(element, device_data, populate_logic.key, populate_logic);
      }
    }
  }, [data]);

  switch (type) {
    case "Series":
      return <PointMachineCircuitDiagram ref={svg_ref} />;
    case "Parallel":
      return <PointMachineParallelCircuitDiagram ref={svg_ref} />;
    case "Single End":
      return <PointMachineSingleEndCircuitDiagram ref={svg_ref} />;
    default:
      return <div>Not Found</div>;
  }
};

export default PointMachineCircuitViewWrapper;
